import React, { useContext } from 'react'
import { catalogProductOptionalDataItem, EcomProduct } from '../../../../models/axiosModel';
import { getProductTableFields, getWhatsAppCatalogDisplayLabel } from './productConstant';
import { useProductStore } from '../../../../store/productStore';
import { Checkbox, Grid, ListItemIcon, Menu, MenuItem, TableCell } from '@mui/material';
import globalStyles from "../../../../assets/styles/global.module.scss";
import dashboardStyles from '../../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import style from '../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss'
import { formatDateAccordingToTimezoneUsingMoment, hasProductManageAccess } from '../../../../utils/constants';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GlobalTooltip from '../../../utility/tooltip/globalTooltip';
import { UserContext, UserContextPayload } from '../../../../context/userContext';

interface MemoizedProductTableRowProps {
    data: EcomProduct,
    index: number,  
    userTimezone: string, 
    anchorEl: null | SVGSVGElement, 
    currentRowIndex: string | null,
    menuItem: { label: string; icon: JSX.Element; action: (data: any) => void }[]; 
    handleMenuOpen: (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowIndex: string) => void;
    handleMenuClose: () => void;  
    formatEComAdditionalInfo: (data: EcomProduct, key: keyof EcomProduct) => string | [string, any][], 
    onClickOfViewAdditionalInfo: (data: EcomProduct) => void,
    onClickOfViewQrCodeData: (data: EcomProduct) => void,
    onClickOfViewOptionalData: (data: EcomProduct) => void,
    isMenuOpen: boolean, 
    isCheckBoxChecked: boolean, 
    handleSelectProduct: (data: EcomProduct) => void,
} 

const MemoizedProductTableRow: React.FC<MemoizedProductTableRowProps> = React.memo((props) => {

    const { data, index, userTimezone, anchorEl, currentRowIndex, menuItem, handleMenuOpen, handleMenuClose, isMenuOpen, formatEComAdditionalInfo, onClickOfViewAdditionalInfo, isCheckBoxChecked, handleSelectProduct, onClickOfViewQrCodeData, onClickOfViewOptionalData } = props;

    const { apiRoutesPermissionAssignment, userDetail } = useContext(UserContext) as UserContextPayload;
    const whatsAppCatalogsData = useProductStore((state) => state.whatsAppCatalogsData);
    const hasWhatsAppCatalogsData = (whatsAppCatalogsData?.length ?? 0) > 0;
    const visibleTableFields = getProductTableFields(hasWhatsAppCatalogsData);

    const isCatalogOptionalDataField = (fieldName?: string) => {
        const normalizedFieldName = String(fieldName ?? "").trim().toLowerCase();
        return normalizedFieldName === "catalog_product_optional_data" || normalizedFieldName === "catalog optional data";
    };

    const renderFieldValue = (field: (typeof visibleTableFields)[number], value: string | number | boolean | catalogProductOptionalDataItem[] | null, displayValue: string | number | boolean | catalogProductOptionalDataItem[] | null): React.ReactNode => {
        const flagAdditionalInfoField = field?.flag_additional_info && value &&  String(value)?.length > 0;
        const flagQrCodeField = field?.name?.toLowerCase() === 'qr code';
        const flagCatalogOptionalDataField = isCatalogOptionalDataField(field?.name);
        const catalogOptionalDataValue: catalogProductOptionalDataItem[] = flagCatalogOptionalDataField && Array.isArray(value) ? value : [];
        const hasCatalogOptionalData = catalogOptionalDataValue.length > 0;

        if(flagAdditionalInfoField || flagQrCodeField || flagCatalogOptionalDataField){
            if ((flagCatalogOptionalDataField && hasCatalogOptionalData) || (value && !flagCatalogOptionalDataField)) {
                return (
                    <Grid 
                        item 
                        sx={{ color: "blue !important", cursor: "pointer" }} 
                        onClick={() => {
                            if(flagAdditionalInfoField) onClickOfViewAdditionalInfo(data);
                            else if(flagQrCodeField) onClickOfViewQrCodeData(data);
                            else if(flagCatalogOptionalDataField) onClickOfViewOptionalData(data);
                        }}
                    >
                        View
                    </Grid>
                );
            }
            return '-';
        }
        if (Array.isArray(displayValue)) {
            return "-";
        }

        return (displayValue && String(displayValue)?.length > 0) ? String(displayValue) : "-";
    }

    return (
        <>  
            {hasProductManageAccess(apiRoutesPermissionAssignment, userDetail) && 
                <TableCell
                    sx={{
                        borderLeft: data.flag_deleted ? '4px solid #CF2C2C' : '4px solid transparent',
                        borderTopLeftRadius: data.flag_deleted ? "10%" : "0",
                        borderBottomLeftRadius: data.flag_deleted ? "10%" : "0", 
                    }}
                    className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.checkBoxCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter}`}>
                    <Checkbox
                        color='secondary'
                        inputProps={{ 'aria-label': 'controlled' }}
                        checked={isCheckBoxChecked}
                        onChange={(e) => handleSelectProduct(data)}
                        disabled={data.flag_deleted}
                        className={style.checkBoxContact}
                    />
                </TableCell>
            }
            {visibleTableFields && visibleTableFields.map((field, ind) => { 
                const value = data[(field?.getCallResponseKey) as keyof EcomProduct] as string | number | boolean | catalogProductOptionalDataItem[] | null;
                const displayValue = field?.getCallResponseKey === "wa_catalog_id"
                    ? getWhatsAppCatalogDisplayLabel(String(value ?? ""), whatsAppCatalogsData)
                    : value;
                return (
                    <>
                        <TableCell 
                            key={ind} 
                            className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.fieldCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter} ${field.getCallResponseKey == "ecom_product_name" && style.firstColumnSticky}`} 
                            sx={{ whiteSpace: "normal !important", wordBreak: "break-word !important", overflowWrap: "anywhere !important", backgroundColor: "white !important"}}
                        >
                            {/* {field?.getCallResponseKey == "ecom_product_image" ?   */}
                            {(field?.flag_file_upload) ?
                                ((value && String(value).length > 0 )) ?
                                <a href={String(value) ?? ""} target='_blank' className={style.truncateProductImageUrl}>
                                    {String(value)}
                                </a> 
                                : "-"
                                :
                                renderFieldValue(field, value, displayValue)
                            }
                        </TableCell>
                    </>
                );
            })}
            <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.fieldCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter}`}>
                {formatDateAccordingToTimezoneUsingMoment(data.updated_at_timestamp || data?.created_at_timestamp, userTimezone)}
            </TableCell>
            {hasProductManageAccess(apiRoutesPermissionAssignment, userDetail) && 
                <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.lastColumnSticky} ${style.moreVertIcon} ${style.checkBoxCellWidth} ${globalStyles.displayFlexStyle} ${globalStyles.alignItemCenter} ${globalStyles.contentEnd}`}>
                    <MoreVertIcon
                        sx={{ cursor: data.flag_deleted ? "not-allowed" : "pointer", opacity: data.flag_deleted ? 0.5 : 1 }}
                        onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
                            if (!data.flag_deleted) handleMenuOpen(e, data.ecom_product_id);
                        }}
                    />
                    <Menu
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl) && (data.ecom_product_id) === currentRowIndex}
                        onClose={handleMenuClose}
                    >
                        {menuItem.map((item, index) => {
                            return (
                                <MenuItem
                                    className={`${globalStyles.menuItemStyle} ${globalStyles.secondaryFontSize}`}
                                    key={index}
                                    disabled={data.flag_deleted}
                                    onClick={(e) => { handleMenuClose(); item.action(data); }}
                                >
                                    <ListItemIcon>
                                        {item.icon}
                                    </ListItemIcon>
                                    <span>
                                        {item.label}
                                    </span>
                                </MenuItem>
                            );
                        })}
                    </Menu>
                </TableCell>
            }
        </>
    )
}, (prev, next) => {
    return (
        prev.data == next.data &&
        prev.isMenuOpen == next.isMenuOpen && 
        prev.isCheckBoxChecked == next.isCheckBoxChecked
    );
} )

export default MemoizedProductTableRow
